/**
 * Add a single IOC. Uses the same `useCreateIoc` mutation as the bulk
 * importer.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { useCreateIoc } from "@/hooks/security/useIocs";
import type { IocKind, IocSeverity } from "@/types/edr-ioc";

const KINDS: IocKind[] = ["sha256", "md5", "ip", "domain", "cert_sha1"];
const SEVS: IocSeverity[] = ["info", "low", "medium", "high", "critical"];

export interface IocAddDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Optional pre-fill (used by Storyline → "Add to IOC blocklist"). */
  defaults?: { kind?: IocKind; value?: string; note?: string };
}

export function IocAddDialog({ open, onOpenChange, defaults }: IocAddDialogProps) {
  const [kind, setKind] = useState<IocKind>("sha256");
  const [value, setValue] = useState("");
  const [severity, setSeverity] = useState<IocSeverity>("high");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const create = useCreateIoc();

  useEffect(() => {
    if (open) {
      setKind(defaults?.kind ?? "sha256");
      setValue(defaults?.value ?? "");
      setSeverity("high");
      setReference("");
      setNote(defaults?.note ?? "");
    }
  }, [open, defaults]);

  const submit = async () => {
    await create.mutateAsync({
      kind,
      value: value.trim(),
      severity,
      reference: reference.trim() || undefined,
      note: note.trim() || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add IOC to blocklist</DialogTitle>
          <DialogDescription>
            Sensors pick up the new IOC on their next heartbeat (≤30s).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as IocKind)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Severity</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as IocSeverity)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVS.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Value</Label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="h-8 font-mono text-xs"
              placeholder={
                kind === "sha256"
                  ? "e9c01…"
                  : kind === "ip"
                  ? "203.0.113.42"
                  : kind === "domain"
                  ? "evil.example.com"
                  : ""
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Reference (optional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="h-8 text-xs"
              placeholder="VT, OTX, internal ticket URL…"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!value.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? "Adding…" : "Add IOC"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
